/**
 * Composant STRIPE Balances - Structure par sections de devises
 * Exploite l'endpoint /financial/owner/stripe-balances
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  alpha,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  Avatar,
  Collapse,
  Paper
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CreditCard as StripeIcon,
  AccountBalance as CurrentIcon,
  Schedule as PendingIcon,
  CheckCircle as TransferredIcon,
  SwapHoriz as ConvertIcon,
  Info as InfoIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Home as HomeIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  MonetizationOn as MonetizationOnIcon,
  Lock as LockIcon,
  Assignment as AssignmentIcon,
  AccountBalance as RefundIcon,
  Gavel as GavelIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { formatCurrency, formatDateTimeUniversal } from '../../utils/formatters';

// Import du composant modal (réutilisé)
import ReservationDetailsModal from './ReservationDetailsModal';

// Fonction pour formater la date avec l'heure (réutilisée de OwnerPaymentHistoryInfinite)
const formatDateWithTime = (dateString) => {
  if (!dateString) return 'Date inconnue';
  
  try {
    const date = new Date(dateString);
    
    // Format DD/MM/YYYY HH:MM
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Date invalide';
  }
};

// Fonction pour traduire les statuts de réservation (réutilisée)
const getReservationStatusLabel = (status) => {
  const statusLabels = {
    'active': 'Active',
    'completed': 'Terminée', 
    'cancelled': 'Annulée',
    'pending': 'En attente',
    'confirmed': 'Confirmée'
  };
  return statusLabels[status] || status || 'Inconnu';
};

// Fonction pour obtenir la couleur et l'icône du statut de réservation (réutilisée)
const getReservationStatusProps = (status) => {
  const statusProps = {
    'active': { color: 'primary', icon: <AssignmentIcon sx={{ fontSize: 16 }} /> },
    'completed': { color: 'success', icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> },
    'cancelled': { color: 'error', icon: <CancelIcon sx={{ fontSize: 16 }} /> },
    'pending': { color: 'warning', icon: <ScheduleIcon sx={{ fontSize: 16 }} /> },
    'confirmed': { color: 'info', icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> }
  };
  return statusProps[status] || { color: 'default', icon: <AssignmentIcon sx={{ fontSize: 16 }} /> };
};

const roundAmount = (value) => Math.round((parseFloat(value || 0) + Number.EPSILON) * 100) / 100;

const getBalanceDetailItemKey = (item) => {
  if (item?.payment_id) return `payment:${item.payment_id}`;

  return [
    item?.reservation_id || 'unknown',
    item?.payment_type || 'unknown',
    item?.date || 'no-date',
    item?.amount || 0
  ].join(':');
};

const dedupeBalanceDetailItems = (items = []) => {
  const seen = new Set();

  return items.filter((item) => {
    const key = getBalanceDetailItemKey(item);
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const getPaymentTypeChipColor = (paymentType) => {
  switch ((paymentType || '').toLowerCase()) {
    case 'rental':
      return 'success';
    case 'commission':
      return 'warning';
    case 'damage_compensation':
      return 'warning';
    case 'deposit':
      return 'info';
    case 'tax':
      return 'secondary';
    case 'dispute_refund':
      return 'error';
    default:
      return 'default';
  }
};

const translatePaymentType = (paymentType) => {
  const types = {
    rental: 'Location',
    deposit: 'Caution',
    commission: 'Commission Krillou',
    tax: 'TVA',
    dispute_refund: 'Remboursement litige',
    damage_compensation: 'Compensation dommages',
  };
  return types[(paymentType || '').toLowerCase()] || paymentType || '—';
};

const BreakdownRowWidget = ({ label, value, negative = false, strong = false }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5, borderBottom: '1px solid #f5f5f5' }}>
    <Typography variant="body2" sx={{ color: '#666' }}>{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: strong ? 800 : 600, color: negative ? '#c62828' : '#1a1a2e', textAlign: 'right' }}>
      {value}
    </Typography>
  </Box>
);

const getNormalizedBreakdown = (breakdown) => {
  if (!breakdown) return null;
  const grossAmount = Number(breakdown.gross_amount || 0);
  const finalPayoutAmount = Number(breakdown.final_payout_amount || 0);
  const stripeFeeAmount = Number(breakdown.stripe_fee_amount || 0);
  if (!breakdown.is_currency_converted) {
    return {
      stripeFeeAmount,
      stripeFeeCurrency: breakdown.stripe_fee_currency,
      netBeforeConversionAmount: Number(breakdown.net_before_conversion_amount || 0),
      netBeforeConversionCurrency: breakdown.net_before_conversion_currency,
      exchangeRate: Number(breakdown.exchange_rate || 0),
      exchangeRateCurrency: breakdown.gross_currency
    };
  }
  if (breakdown.type === 'damage_compensation' && breakdown.gross_currency && breakdown.gross_currency !== 'EUR' && finalPayoutAmount > 0 && grossAmount > 0) {
    const grossEquivalentInEur = finalPayoutAmount + Math.max(0, stripeFeeAmount);
    const displayExchangeRate = grossEquivalentInEur > 0 ? grossAmount / grossEquivalentInEur : 0;
    return {
      stripeFeeAmount: Math.max(0, stripeFeeAmount) * displayExchangeRate,
      stripeFeeCurrency: breakdown.gross_currency,
      netBeforeConversionAmount: finalPayoutAmount * displayExchangeRate,
      netBeforeConversionCurrency: breakdown.gross_currency,
      exchangeRate: displayExchangeRate,
      exchangeRateCurrency: breakdown.gross_currency
    };
  }
  return {
    stripeFeeAmount,
    stripeFeeCurrency: breakdown.stripe_fee_currency,
    netBeforeConversionAmount: Number(breakdown.net_before_conversion_amount || 0),
    netBeforeConversionCurrency: breakdown.net_before_conversion_currency,
    exchangeRate: Number(breakdown.exchange_rate || 0),
    exchangeRateCurrency: breakdown.gross_currency
  };
};

const getPayoutDisplayAmount = (reservation) => {
  const breakdown = reservation?.payout_breakdown;
  if (!breakdown) {
    return roundAmount(reservation?.payout_net_amount || reservation?.net_amount || reservation?.gross_amount || reservation?.other_amount || 0);
  }

  // For damage_compensation with currency conversion: show the EUR transfer amount
  // (the card will use display_currency to show the correct currency symbol)
  if (breakdown.type === 'damage_compensation') {
    return roundAmount(breakdown.final_payout_amount || reservation?.payout_net_amount || 0);
  }

  // For regular payouts with currency conversion: show the original-currency net amount
  // If a penalty was applied, use final_payout_amount × exchange_rate to reflect post-penalty DZD amount
  if (breakdown.is_currency_converted && breakdown.net_before_conversion_amount) {
    if (breakdown.has_penalty && breakdown.final_payout_amount && breakdown.exchange_rate > 1) {
      return roundAmount(breakdown.final_payout_amount * breakdown.exchange_rate);
    }
    return roundAmount(breakdown.net_before_conversion_amount);
  }

  return roundAmount(breakdown.final_payout_amount || reservation?.payout_net_amount || 0);
};

// Get the correct display currency for a reservation card
const getPayoutDisplayCurrency = (reservation, fallbackCurrency) => {
  const breakdown = reservation?.payout_breakdown;
  if (!breakdown) return fallbackCurrency;
  // For damage_compensation: show in the transfer currency (EUR)
  if (breakdown.type === 'damage_compensation') {
    return breakdown.final_payout_currency || fallbackCurrency;
  }
  // For regular payouts: show in the original currency
  return breakdown.gross_currency || fallbackCurrency;
};

const OwnerStripeBalancesWidget = () => {
  const { t } = useTranslation('financial');
  const { user } = useAuth();
  const theme = useTheme();

  // États du composant
  const [stripeData, setStripeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [targetCurrency, setTargetCurrency] = useState('EUR');
  
  // États pour les détails et modaux
  const [expandedBalance, setExpandedBalance] = useState(null);
  const [expandedDetails, setExpandedDetails] = useState({});
  const [expandedPenalties, setExpandedPenalties] = useState({});
  const [selectedReservationId, setSelectedReservationId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // État pour l'accordéon principal
  const [isMainExpanded, setIsMainExpanded] = useState(true);
  
  // États pour les détails des soldes
  const [balanceDetails, setBalanceDetails] = useState({});
  const [detailsLoading, setDetailsLoading] = useState({});
  
  // État pour le modal d'information
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [infoModalContent, setInfoModalContent] = useState({});

  /**
   * Charger les données STRIPE
   */
  const loadStripeBalances = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);

      console.log('📊 [StripeBalances] Fetching STRIPE balances data');

      const params = new URLSearchParams();
      if (targetCurrency) params.append('targetCurrency', targetCurrency);

      const response = await api.get(`/financial/owner/stripe-balances?${params.toString()}`);

      if (!response || !response.data) {
        throw new Error(t('globalStats.error'));
      }

      // Extraire les données selon la structure backend
      let data;
      if (response.data.success && response.data.data) {
        data = response.data.data;
      } else if (response.data && typeof response.data === 'object') {
        data = response.data;
      } else {
        throw new Error(t('globalStats.error'));
      }

      console.log('✅ [StripeBalances] STRIPE data loaded:', data);

      setStripeData(data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('❌ [StripeBalances] Error loading STRIPE balances:', err);
      setError(err.message || t('globalStats.error'));
    } finally {
      setLoading(false);
    }
  }, [targetCurrency, t]);

  // Effet de chargement initial
  useEffect(() => {
    if (user) {
      loadStripeBalances();
    }
  }, [user, loadStripeBalances]);

  // Gestionnaire de rafraîchissement
  const handleRefresh = () => {
    loadStripeBalances(false);
  };

  // Gestionnaire d'expansion des détails de transaction (réutilisé de OwnerPaymentHistoryInfinite)
  const handleToggleExpansion = useCallback((transactionId) => {
    setExpandedDetails(prev => ({
      ...prev,
      [transactionId]: !prev[transactionId]
    }));
  }, []);

  const handleTogglePenalties = useCallback((key) => {
    setExpandedPenalties(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // Gestionnaire d'ouverture du modal de détails de réservation (réutilisé)
  const handleShowReservationDetails = useCallback((reservationId) => {
    setSelectedReservationId(reservationId);
    setIsModalOpen(true);
  }, []);

  // Gestionnaire de fermeture du modal (réutilisé)
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedReservationId(null);
  }, []);

  // Gestionnaire de changement de devise cible
  const handleTargetCurrencyChange = (event) => {
    setTargetCurrency(event.target.value);
  };

  // Gestionnaire d'ouverture du modal d'information
  const openInfoModal = (infoType, currency) => {
    const infoContent = {
      currentBalance: {
        title: t('stripe.info.currentBalance.title', 'Solde actuel'),
        content: t('stripe.info.currentBalance.content', {currency})
      },
      pendingBalance: {
        title: t('stripe.info.pendingBalance.title', 'Solde à venir'),
        content: t('stripe.info.pendingBalance.content', {currency})
      },
      transferredBalance: {
        title: t('stripe.info.transferredBalance.title', 'Solde transféré'),
        content: t('stripe.info.transferredBalance.content', {currency})
      }
    };

    setInfoModalContent(infoContent[infoType] || {});
    setIsInfoModalOpen(true);
  };

  // Gestionnaire de fermeture du modal d'information
  const closeInfoModal = () => {
    setIsInfoModalOpen(false);
    setInfoModalContent({});
  };

  // Gestionnaire de clic sur une carte de solde
  const handleBalanceClick = async (currency, balanceType) => {
    const balanceKey = `${currency}_${balanceType}`;
    
    // Si déjà expansé, fermer
    if (expandedBalance === balanceKey) {
      setExpandedBalance(null);
      return;
    }
    
    // Ouvrir et charger les détails
    setExpandedBalance(balanceKey);
    
    // Charger les détails si pas déjà chargés
    if (!balanceDetails[balanceKey]) {
      await loadBalanceDetails(currency, balanceType, 1);
    }
  };

  // Charger les détails d'un solde
  const loadBalanceDetails = async (currency, balanceType, page = 1) => {
    const balanceKey = `${currency}_${balanceType}`;
    
    try {
      setDetailsLoading(prev => ({ ...prev, [balanceKey]: true }));

      console.log(`📊 [BalanceDetails] Loading details for ${currency} ${balanceType}, page ${page}`);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '8'  // Augmenté à 8 pour une meilleure pagination
      });

      const fullUrl = `/financial/owner/stripe-balance-details/${currency}/${balanceType}?${params.toString()}`;
      console.log(`🌐 [BalanceDetails] Full API URL:`, fullUrl);

      const response = await api.get(fullUrl);

      console.log(`📦 [BalanceDetails] Raw API response:`, {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        data: response.data
      });

      if (!response.data) {
        console.error(`❌ [BalanceDetails] No response.data received`);
        throw new Error(t('globalStats.error'));
      }

      let data;
      if (response.data.success && response.data.data) {
        console.log(`✅ [BalanceDetails] Using response.data.data structure`);
        data = response.data.data;
      } else if (response.data && response.data.items) {
        console.log(`✅ [BalanceDetails] Using direct response.data structure`);
        data = response.data;
      } else {
        console.error(`❌ [BalanceDetails] Unexpected data structure:`, response.data);
        throw new Error(t('globalStats.error'));
      }

      console.log(`✅ [BalanceDetails] Details loaded for ${currency} ${balanceType}:`, {
        itemsCount: data.items?.length || 0,
        pagination: data.pagination,
        rental_sum: data.rental_sum,
        firstItem: data.items?.[0] || 'No items'
      });

      // Mettre à jour les détails (pagination infinie)
      setBalanceDetails(prev => {
        const existingData = prev[balanceKey];
        console.log(`🔄 [BalanceDetails] Updating balance details:`, {
          balanceKey,
          page,
          hasExistingData: !!existingData,
          newItemsCount: data.items?.length || 0,
          hasMore: data.pagination?.hasMore
        });
        
        if (page === 1 || !existingData) {
          // Première page ou reset - remplacer les données
          return {
            ...prev,
            [balanceKey]: {
              ...data,
              items: dedupeBalanceDetailItems(data.items || [])
            }
          };
        } else {
          // Pages suivantes - ajouter aux données existantes (pagination infinie)
          const mergedItems = dedupeBalanceDetailItems([
            ...(existingData.items || []),
            ...(data.items || [])
          ]);

          return {
            ...prev,
            [balanceKey]: {
              ...data,
              items: mergedItems,
              pagination: {
                ...data.pagination,
                page: data.pagination.page
              }
            }
          };
        }
      });

    } catch (err) {
      console.error('❌ [BalanceDetails] Error loading details:', err);
      setError(err.message || t('globalStats.error'));
    } finally {
      setDetailsLoading(prev => ({ ...prev, [balanceKey]: false }));
    }
  };

  // Charger plus de détails (pagination infinie)
  const loadMoreDetails = async (currency, balanceType) => {
    const balanceKey = `${currency}_${balanceType}`;
    const currentData = balanceDetails[balanceKey];
    
    if (!currentData?.pagination?.hasMore) {
      console.log(`🚫 [LoadMore] Cannot load more for ${balanceKey}:`, {
        hasData: !!currentData,
        hasMore: currentData?.pagination?.hasMore,
        pagination: currentData?.pagination
      });
      return;
    }
    
    const nextPage = currentData.pagination.page + 1;
    console.log(`📄 [LoadMore] Loading page ${nextPage} for ${balanceKey}`);
    
    // S'assurer qu'on ne charge pas déjà
    if (detailsLoading[balanceKey]) {
      console.log(`⏳ [LoadMore] Already loading for ${balanceKey}, skipping...`);
      return;
    }
    
    await loadBalanceDetails(currency, balanceType, nextPage);
  };

  // Composant de métrique pour chaque solde (simplifié)
  const BalanceMetric = ({ title, value, currency, icon, color = 'primary', details = null, infoType, balanceType }) => {
    const balanceKey = `${currency}_${balanceType}`;
    const isExpanded = expandedBalance === balanceKey;

    return (
      <Box 
        sx={{ 
          p: 2,
          border: 1,
          borderColor: isExpanded ? `${color}.main` : 'divider',
          borderRadius: 1,
          backgroundColor: alpha(theme.palette[color].main, isExpanded ? 0.1 : 0.05),
          transition: 'all 0.2s ease-in-out',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: alpha(theme.palette[color].main, 0.15),
            transform: 'translateY(-1px)',
            boxShadow: theme.shadows[2]
          }
        }}
        onClick={() => handleBalanceClick(currency, balanceType)}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box color={theme.palette[color].main}>
            {icon}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip 
              label={currency} 
              size="small" 
              color={color}
              variant="outlined"
            />
            {infoType && (
              <Tooltip title={t('stripe.infoTooltip', 'Cliquer pour plus d\'informations')}>
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    openInfoModal(infoType, currency);
                  }}
                  sx={{ color: theme.palette.text.secondary }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" sx={{ color: theme.palette[color].main }}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        <Typography variant="h6" fontWeight="bold" color={`${color}.main`} mb={0.5}>
          {formatCurrency(value, currency)}
        </Typography>
        
        <Typography variant="subtitle2" color="text.primary" fontWeight="medium" mb={1}>
          {title}
        </Typography>
        
        {details && (
          <Typography variant="caption" color="text.secondary">
            {details}
          </Typography>
        )}

        {isExpanded && (
          <Typography variant="caption" color={`${color}.main`} fontWeight="medium" sx={{ display: 'block', mt: 1 }}>
            {t('stripe.balance.viewDetails')}
          </Typography>
        )}
      </Box>
    );
  };

  // Composant section par devise
  const CurrencySection = ({ currency, balanceData }) => (
    <Card 
      elevation={2}
      sx={{ 
        mb: 2,
        border: 1,
        borderColor: 'divider',
        '&:hover': {
          boxShadow: theme.shadows[4]
        }
      }}
    >
      <CardContent>
        {/* En-tête de la section */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" fontWeight="bold" color="primary">
              {currency === 'DZD' ? '🇩🇿' : currency === 'EUR' ? '🇪🇺' : currency === 'USD' ? '🇺🇸' : '🌍'} {currency}
            </Typography>
          </Box>
          <Chip 
            label={t('stripe.currencyTransactions', {count: balanceData.payment_details.completed_count + balanceData.payment_details.pending_count})}
            size="small"
            variant="outlined"
          />
        </Box>

        {/* Métriques des soldes */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <BalanceMetric
              title={t('stripe.balance.current', 'Solde actuel')}
              value={balanceData.current_balance}
              currency={currency}
              icon={<CurrentIcon />}
              color="success"
              details={t('stripe.balance.completedCount', {count: balanceData.payment_details.completed_count})}
              infoType="currentBalance"
              balanceType="current"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <BalanceMetric
              title={t('stripe.balance.pending', 'Solde à venir')}
              value={balanceData.pending_balance}
              currency={currency}
              icon={<PendingIcon />}
              color="warning"
              details={t('stripe.balance.pendingCount', {count: balanceData.payment_details.pending_count})}
              infoType="pendingBalance"
              balanceType="pending"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <BalanceMetric
              title={t('stripe.balance.transferred', 'Solde transféré')}
              value={balanceData.transferred_balance}
              currency={currency}
              icon={<TransferredIcon />}
              color="info"
              details={t('stripe.balance.transferredCount', {count: balanceData.payment_details.transferred_count || 0})}
              infoType="transferredBalance"
              balanceType="transferred"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  // Rendu conditionnel pour les états de chargement et d'erreur
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <CircularProgress size={40} />
            <Typography variant="body2" mt={2} color="text.secondary">
              {t('stripe.loading', 'Chargement des soldes STRIPE...')}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert 
            severity="error" 
            action={
              <Button color="inherit" size="small" onClick={handleRefresh}>
                {t('common.retry', 'Réessayer')}
              </Button>
            }
          >
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!stripeData) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {t('stripe.noData', 'Aucune donnée STRIPE disponible')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        {/* En-tête principal - Accordéon */}
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center" 
          mb={2}
          onClick={() => setIsMainExpanded(!isMainExpanded)}
          sx={{
            cursor: 'pointer',
            p: 1,
            borderRadius: 1,
            background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
            color: 'white',
            '&:hover': {
              background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
            }
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <StripeIcon sx={{ color: 'white' }} />
            <Box>
              <Typography variant="h5" fontWeight="bold" sx={{ color: 'white' }}>
                {t('stripe.title', 'Soldes STRIPE')}
              </Typography>
              {lastUpdated && isMainExpanded && (
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  {t('common.lastUpdated', 'Dernière mise à jour')}: {formatDateTimeUniversal(lastUpdated)}
                </Typography>
              )}
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={(e) => { e.stopPropagation(); handleRefresh(); }} sx={{ color: 'white' }}>
              <RefreshIcon />
            </IconButton>
            <IconButton sx={{ color: 'white' }}>
              {isMainExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Contenu rétractable */}
        <Collapse in={isMainExpanded}>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {t('stripe.description', 'Gestion des revenus provenant des paiements en ligne via STRIPE. L\'argent transite par la plateforme avant transfert automatique.')}
          </Typography>

          {/* Sections par devise */}
          {Object.entries(stripeData.stripe_balances).map(([currency, balanceData]) => (
            <React.Fragment key={currency}>
              <CurrencySection 
                currency={currency} 
                balanceData={balanceData} 
              />
              
              {/* Section des détails expansés pour cette devise */}
              {expandedBalance && expandedBalance.startsWith(currency + '_') && (
              <Card 
                sx={{ 
                  mb: 3,
                  border: 2,
                  borderColor: 'primary.main',
                  backgroundColor: alpha(theme.palette.primary.main, 0.02)
                }}
              >
                <CardContent>
                  {(() => {
                    const [currencyFromBalance, balanceType] = expandedBalance.split('_');
                    const detailsData = balanceDetails[expandedBalance];
                    const isLoadingDetails = detailsLoading[expandedBalance];
                    
                    return (
                      <>
                        {/* En-tête des détails */}
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Typography variant="h6" fontWeight="bold" color="primary">
                              {currencyFromBalance} - {balanceType === 'current' ? t('stripe.details.typeCurrent') : balanceType === 'pending' ? t('stripe.details.typePending') : t('stripe.details.typeTransferred')}
                            </Typography>
                            {detailsData && (
                              <Chip 
                                label={t('stripe.details.transactionsCount', {count: detailsData.pagination.total})}
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Box>
                          <IconButton 
                            onClick={() => setExpandedBalance(null)}
                            color="primary"
                          >
                            <ExpandLessIcon />
                          </IconButton>
                        </Box>

                        {/* Contenu des détails */}
                        <Box>
                          {isLoadingDetails ? (
                            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                              <CircularProgress size={32} color="primary" />
                              <Typography variant="body1" ml={2} color="text.secondary">
                                {t('stripe.details.loading')}
                              </Typography>
                            </Box>
                          ) : detailsData && detailsData.items.length > 0 ? (
                            <>
                              {/* Groupement des transactions par réservation et affichage */}
                              {(() => {
                                const uniqueItems = dedupeBalanceDetailItems(detailsData.items || []);

                                console.log(`🎯 [BalanceDetails] Rendering details for ${expandedBalance}:`, {
                                  detailsData,
                                  itemsCount: detailsData.items.length,
                                  uniqueItemsCount: uniqueItems.length,
                                  firstItem: uniqueItems[0],
                                  allItems: uniqueItems
                                });

                                // Grouper par owner_payout_id (si disponible) ou reservation_id
                                // Chaque payout = une carte, damage_compensation séparé
                                const groupedByReservation = uniqueItems.reduce((acc, item) => {
                                  const groupKey = item.owner_payout_id || item.reservation_id || 'unknown';
                                  const isDamageComp = item.payout_type === 'damage_compensation';
                                  const amount = roundAmount(item.amount || 0);
                                  const paymentType = String(item.payment_type || 'unknown').toLowerCase();
                                  
                                  if (!acc[groupKey]) {
                                    acc[groupKey] = {
                                      owner_payout_id: item.owner_payout_id,
                                      reservation_id: item.reservation_id,
                                      listing_title: item.listing_title,
                                      renter_name: item.renter_name,
                                      start_date: item.start_date,
                                      end_date: item.end_date,
                                      reservation_status: item.reservation_status,
                                      currency: currencyFromBalance,
                                      is_damage_compensation: isDamageComp,
                                      payout_net_amount: parseFloat(item.payout_net_amount || 0),
                                      payout_breakdown: item.payout_breakdown || null,
                                      display_currency: null, // Set after total_amount computation
                                      transactions: [],
                                      total_amount: 0,
                                      gross_amount: 0,
                                      rental_amount: 0,
                                      tax_amount: 0,
                                      commission_amount: 0,
                                      deposit_amount: 0,
                                      dispute_refund_amount: 0,
                                      other_amount: 0,
                                      net_amount: 0
                                    };
                                  }
                                  
                                  acc[groupKey].transactions.push({ ...item, amount });

                                  switch (paymentType) {
                                    case 'rental':
                                      acc[groupKey].rental_amount = roundAmount(acc[groupKey].rental_amount + amount);
                                      acc[groupKey].net_amount = roundAmount(acc[groupKey].net_amount + amount);
                                      break;
                                    case 'tax':
                                      acc[groupKey].tax_amount = roundAmount(acc[groupKey].tax_amount + amount);
                                      break;
                                    case 'commission':
                                      acc[groupKey].commission_amount = roundAmount(acc[groupKey].commission_amount + amount);
                                      break;
                                    case 'deposit':
                                      acc[groupKey].deposit_amount = roundAmount(acc[groupKey].deposit_amount + amount);
                                      break;
                                    case 'dispute_refund':
                                      acc[groupKey].dispute_refund_amount = roundAmount(acc[groupKey].dispute_refund_amount + amount);
                                      break;
                                    case 'damage_compensation':
                                      // Affiché à titre informatif uniquement — n'affecte pas les calculs
                                      break;
                                    default:
                                      acc[groupKey].other_amount = roundAmount(acc[groupKey].other_amount + amount);
                                      break;
                                  }

                                  acc[groupKey].gross_amount = roundAmount(
                                    acc[groupKey].rental_amount +
                                    acc[groupKey].tax_amount +
                                    acc[groupKey].commission_amount
                                  );

                                  // pending/transferred: always display the payout amount converted to the bucket currency
                                  acc[groupKey].total_amount = item.payout_breakdown
                                    ? getPayoutDisplayAmount(acc[groupKey])
                                    : (isDamageComp
                                      ? roundAmount(acc[groupKey].payout_net_amount)
                                      : roundAmount((acc[groupKey].net_amount - acc[groupKey].dispute_refund_amount) || acc[groupKey].gross_amount || acc[groupKey].other_amount));
                                  
                                  // Compute the correct display currency for this card
                                  acc[groupKey].display_currency = getPayoutDisplayCurrency(acc[groupKey], currencyFromBalance);
                                  
                                  return acc;
                                }, {});

                                console.log(`🗂️ [BalanceDetails] Grouped by reservation:`, groupedByReservation);

                                // Convertir en array et trier par date
                                const reservations = Object.values(groupedByReservation)
                                  .map((reservation) => ({
                                    ...reservation,
                                    transactions: reservation.transactions.sort((a, b) => {
                                      const dateA = new Date(a.date || 0);
                                      const dateB = new Date(b.date || 0);
                                      return dateB - dateA;
                                    })
                                  }))
                                  .sort((a, b) => {
                                  const dateA = new Date(a.start_date || a.transactions[0]?.date || 0);
                                  const dateB = new Date(b.start_date || b.transactions[0]?.date || 0);
                                  return dateB - dateA;
                                  });

                                console.log(`📊 [BalanceDetails] Final reservations array:`, {
                                  reservationsCount: reservations.length,
                                  reservations
                                });

                                // Build a map: reservation_id → damage_comp card
                                // So regular payout cards can reference the real damage_comp payment
                                // without touching the backend or altering any totals
                                const damageCompByReservationId = {};
                                for (const r of reservations) {
                                  if (r.is_damage_compensation && r.reservation_id) {
                                    damageCompByReservationId[r.reservation_id] = r;
                                  }
                                }

                                const cardsToRender = reservations.map((reservation, index) => {
                                  const reservationKey = `${expandedBalance}_${reservation.owner_payout_id || reservation.reservation_id}`;
                                  const isExpanded = expandedDetails[reservationKey];
                                  
                                  return (
                                    <Card 
                                      key={reservationKey} 
                                      sx={{ 
                                        mb: 2, 
                                        border: '1px solid', 
                                        borderColor: reservation.is_damage_compensation ? 'warning.main' : 'divider'
                                      }}
                                    >
                                      {/* En-tête de la réservation - cliquable pour expansion */}
                                      <CardContent 
                                        sx={{ 
                                          cursor: 'pointer',
                                          '&:hover': { backgroundColor: alpha(theme.palette.action.hover, 0.04) }
                                        }}
                                        onClick={() => handleToggleExpansion(reservationKey)}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <Avatar sx={{ bgcolor: reservation.is_damage_compensation ? theme.palette.warning.main : theme.palette.primary.main, mr: 2 }}>
                                            {reservation.is_damage_compensation ? <GavelIcon /> : <MoneyIcon />}
                                          </Avatar>
                                          
                                          <Box sx={{ flex: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                              <Box>
                                                <Typography variant="subtitle1" fontWeight="medium">
                                                  {reservation.is_damage_compensation
                                                    ? `🔨 ${t('stripe.details.damageCompensation', 'Compensation dommages')} — ${reservation.listing_title || ''}`
                                                    : (reservation.listing_title || t('stripe.details.unknownListing'))}
                                                </Typography>
                                                {reservation.start_date && (
                                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                    <CalendarIcon sx={{ fontSize: 12, mr: 0.5 }} />
                                                    {t('stripe.details.dateRange', {
                                                      from: new Date(reservation.start_date).toLocaleDateString(),
                                                      to: new Date(reservation.end_date).toLocaleDateString()
                                                    })}
                                                  </Typography>
                                                )}
                                                {reservation.renter_name && (
                                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                    <PersonIcon sx={{ fontSize: 12, mr: 0.5 }} />
                                                    {reservation.renter_name}
                                                  </Typography>
                                                )}
                                                {reservation.reservation_id && reservation.reservation_id !== 'unknown' && (
                                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.65rem' }}>
                                                    <AssignmentIcon sx={{ fontSize: 11, mr: 0.5, verticalAlign: 'middle' }} />
                                                    {t('stripe.details.reservationRef', 'Réf:')} {reservation.reservation_id.substring(0, 8)}…
                                                  </Typography>
                                                )}
                                              </Box>
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Chip 
                                                  label={t('stripe.details.transactionCount', { count: reservation.transactions.length })}
                                                  size="small" 
                                                  variant="outlined"
                                                  color="primary"
                                                />
                                                {reservation.deposit_amount > 0 && (
                                                  <Chip
                                                    label={t('financial:depositLabel', {
                                                      amount: formatCurrency(reservation.deposit_amount, currencyFromBalance),
                                                      defaultValue: `Dépôt: ${formatCurrency(reservation.deposit_amount, currencyFromBalance)}`
                                                    })}
                                                    size="small"
                                                    variant="outlined"
                                                    color="info"
                                                  />
                                                )}
                                                <Typography variant="h6" color="primary.main" fontWeight="bold">
                                                  {formatCurrency(reservation.total_amount, reservation.display_currency || currencyFromBalance)}
                                                </Typography>
                                                <IconButton size="small" sx={{ ml: 1 }}>
                                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                              </Box>
                                            </Box>
                                          </Box>
                                        </Box>
                                      </CardContent>

                                      {/* Détails expansés */}
                                      <Collapse in={isExpanded}>
                                        <CardContent sx={{ pt: 0 }}>
                                          <Divider sx={{ mb: 2 }} />
                                          
                                          {/* Breakdown financier — style PayoutCard */}
                                          {reservation.payout_breakdown && (() => {
                                            const bd = reservation.payout_breakdown;
                                            const norm = getNormalizedBreakdown(bd);
                                            return (
                                              <Paper elevation={0} sx={{ mb: 2, p: 1.5, borderRadius: 2, background: '#fafafa', border: '1px solid #eee' }}>
                                                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                                  {t('stripe.details.financialSummary', 'Détail financier')}
                                                </Typography>
                                                <BreakdownRowWidget
                                                  label={t('payouts.grossAmount', 'Montant brut')}
                                                  value={formatCurrency(bd.gross_amount, bd.gross_currency)}
                                                />
                                                {bd.type === 'damage_compensation' ? (
                                                  <BreakdownRowWidget
                                                    label={t('payouts.stripeFees', 'Frais Stripe')}
                                                    value={`- ${formatCurrency(norm?.stripeFeeAmount || 0, norm?.stripeFeeCurrency || bd.stripe_fee_currency)}`}
                                                    negative
                                                  />
                                                ) : (
                                                  <>
                                                    <BreakdownRowWidget
                                                      label={t('payouts.commission', 'Commission Krillou')}
                                                      value={`- ${formatCurrency(bd.commission_amount, bd.commission_currency)}`}
                                                      negative
                                                    />
                                                    <BreakdownRowWidget
                                                      label={t('payouts.tax', 'TVA')}
                                                      value={`- ${formatCurrency(bd.tax_amount, bd.tax_currency)}`}
                                                      negative
                                                    />
                                                  </>
                                                )}
                                                <BreakdownRowWidget
                                                  label={t('payouts.netBeforeConversion', 'Net avant conversion')}
                                                  value={formatCurrency(norm?.netBeforeConversionAmount || 0, norm?.netBeforeConversionCurrency || bd.net_before_conversion_currency)}
                                                />
                                                {balanceType !== 'current' && bd.has_penalty && bd.penalty_amount > 0 && (
                                                  <BreakdownRowWidget
                                                    label={t('payouts.penaltyDeduction', 'Pénalité déduite')}
                                                    value={`- ${formatCurrency(bd.penalty_amount, bd.penalty_currency)}`}
                                                    negative
                                                  />
                                                )}
                                                {norm?.exchangeRate > 0 && norm?.exchangeRateCurrency !== 'EUR' && (
                                                  <BreakdownRowWidget
                                                    label={t('payouts.exchangeRate', 'Taux de change')}
                                                    value={`1 EUR = ${Number(norm.exchangeRate).toFixed(4)} ${norm.exchangeRateCurrency}`}
                                                  />
                                                )}
                                                <Divider sx={{ my: 0.5 }} />
                                                <BreakdownRowWidget
                                                  label={t('payouts.finalPayout', 'Montant versé')}
                                                  value={formatCurrency(bd.final_payout_amount, bd.final_payout_currency)}
                                                  strong
                                                />
                                              </Paper>
                                            );
                                          })()}

                                          {/* Note info — carte damage_compensation */}
                                          {reservation.is_damage_compensation && (
                                            <Alert severity="info" icon={<GavelIcon fontSize="inherit" />} sx={{ mb: 2, borderRadius: 2 }}>
                                              <Typography variant="caption">
                                                Ce virement de compensation dommages est géré séparément via son propre virement Stripe. Il est affiché ici car il est directement associé à la réservation.
                                              </Typography>
                                            </Alert>
                                          )}

                                          {/* Pénalités — accordéon (soldes à venir et transférés uniquement) */}
                                          {balanceType !== 'current' &&
                                            reservation.payout_breakdown?.has_penalty &&
                                            Array.isArray(reservation.payout_breakdown?.penalty_deductions) &&
                                            reservation.payout_breakdown.penalty_deductions.length > 0 && (
                                            <Box sx={{ mb: 2 }}>
                                              <Button
                                                size="small"
                                                color="error"
                                                onClick={() => handleTogglePenalties(reservationKey)}
                                                endIcon={
                                                  <ExpandMoreIcon
                                                    sx={{
                                                      transform: expandedPenalties[reservationKey] ? 'rotate(180deg)' : 'rotate(0deg)',
                                                      transition: 'transform 0.2s ease'
                                                    }}
                                                  />
                                                }
                                                startIcon={<GavelIcon />}
                                                sx={{ px: 0, fontWeight: 700 }}
                                              >
                                                {expandedPenalties[reservationKey]
                                                  ? t('payouts.hidePenalties', 'Masquer les pénalités')
                                                  : t('payouts.showPenalties', `Pénalités (${reservation.payout_breakdown.penalty_count})`)}
                                              </Button>
                                              <Collapse in={expandedPenalties[reservationKey]} timeout="auto" unmountOnExit>
                                                <Paper elevation={0} sx={{ mt: 1, p: 1.5, borderRadius: 2, background: '#fff8e1', border: '1px solid #f57c00' }}>
                                                  {reservation.payout_breakdown.penalty_deductions.map((penalty, pIdx) => (
                                                    <Box
                                                      key={penalty.penalty_id || pIdx}
                                                      sx={{
                                                        mb: pIdx < reservation.payout_breakdown.penalty_deductions.length - 1 ? 1.5 : 0,
                                                        pb: pIdx < reservation.payout_breakdown.penalty_deductions.length - 1 ? 1.5 : 0,
                                                        borderBottom: pIdx < reservation.payout_breakdown.penalty_deductions.length - 1 ? '1px solid #ffe0b2' : 'none'
                                                      }}
                                                    >
                                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#c62828' }}>
                                                          − {formatCurrency(penalty.deducted_amount, reservation.payout_breakdown.penalty_currency || currencyFromBalance)}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#888' }}>
                                                          {penalty.date ? new Date(penalty.date).toLocaleDateString('fr-FR') : '—'}
                                                        </Typography>
                                                      </Box>
                                                      {penalty.reason && (
                                                        <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                                                          {penalty.reason}
                                                        </Typography>
                                                      )}
                                                      <Box sx={{ display: 'flex', gap: 2 }}>
                                                        <Typography variant="caption" sx={{ color: '#888' }}>
                                                          Avant : {formatCurrency(penalty.previous_net_amount, reservation.payout_breakdown.penalty_currency || currencyFromBalance)}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                                                          → Après : {formatCurrency(penalty.new_net_amount, reservation.payout_breakdown.penalty_currency || currencyFromBalance)}
                                                        </Typography>
                                                      </Box>
                                                    </Box>
                                                  ))}
                                                </Paper>
                                              </Collapse>
                                            </Box>
                                          )}

                                          {/* Liste des transactions */}
                                          <Typography variant="subtitle2" fontWeight="bold" color="text.primary" sx={{ mb: 1 }}>
                                            {t('stripe.details.transactionsLabel')}
                                          </Typography>
                                          {reservation.transactions.map((transaction, txIndex) => {
                                            const isDisputeRefund = (transaction.payment_type || '').toLowerCase() === 'dispute_refund';
                                            const isDamageCompTx = (transaction.payment_type || '').toLowerCase() === 'damage_compensation';
                                            const showDamageCompNote = isDamageCompTx && !reservation.is_damage_compensation;
                                            return (
                                              <React.Fragment key={`${reservationKey}_tx_${txIndex}`}>
                                                <Paper 
                                                  elevation={1} 
                                                  sx={{ 
                                                    p: 2, 
                                                    mb: showDamageCompNote ? 0 : 1,
                                                    backgroundColor: isDamageCompTx
                                                      ? alpha(theme.palette.warning.main, 0.04)
                                                      : isDisputeRefund
                                                        ? alpha(theme.palette.error.main, 0.04)
                                                        : alpha(theme.palette.primary.main, 0.02),
                                                    border: '1px solid',
                                                    borderColor: isDamageCompTx
                                                      ? alpha(theme.palette.warning.main, 0.3)
                                                      : isDisputeRefund
                                                        ? alpha(theme.palette.error.main, 0.3)
                                                        : alpha(theme.palette.primary.main, 0.1)
                                                  }}
                                                >
                                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Box sx={{ flex: 1 }}>
                                                      <Typography variant="body2" fontWeight="bold" color={isDamageCompTx ? 'warning.dark' : isDisputeRefund ? 'error.main' : 'inherit'}>
                                                          {isDisputeRefund ? '− ' : ''}{formatCurrency(transaction.amount, isDamageCompTx ? (transaction.currency || currencyFromBalance) : (reservation.display_currency || currencyFromBalance))}
                                                      </Typography>
                                                      <Typography variant="caption" color={isDamageCompTx ? 'warning.main' : isDisputeRefund ? 'error.light' : 'text.secondary'}>
                                                        {translatePaymentType(transaction.payment_type)} • {formatDateWithTime(transaction.date)}
                                                      </Typography>
                                                    </Box>
                                                    <Chip 
                                                      label={translatePaymentType(transaction.payment_type)} 
                                                      size="small" 
                                                      color={getPaymentTypeChipColor(transaction.payment_type)}
                                                      variant="outlined"
                                                    />
                                                  </Box>
                                                </Paper>
                                                {showDamageCompNote && (
                                                  <Box sx={{ mb: 1, px: 1.5, py: 0.75, bgcolor: alpha(theme.palette.warning.main, 0.06), borderRadius: '0 0 4px 4px', border: '1px solid', borderTop: 'none', borderColor: alpha(theme.palette.warning.main, 0.25) }}>
                                                    <Typography variant="caption" sx={{ color: 'warning.dark', fontStyle: 'italic' }}>
                                                      ℹ️ Compensation dommages — gérée séparément via son propre virement Stripe. Affichée à titre d'information car elle fait partie de la réservation.
                                                    </Typography>
                                                  </Box>
                                                )}
                                              </React.Fragment>
                                            );
                                          })}

                                          {/* Ligne damage_compensation — information uniquement, sans impact sur les totaux */}
                                          {!reservation.is_damage_compensation &&
                                            balanceType !== 'current' &&
                                              !reservation.transactions.some((transaction) => (transaction.payment_type || '').toLowerCase() === 'damage_compensation') &&
                                            damageCompByReservationId[reservation.reservation_id] && (() => {
                                              const dc = damageCompByReservationId[reservation.reservation_id];
                                              const dcTx = dc.transactions[0]; // le payment réel
                                              if (!dcTx) return null;
                                              const dcBd = dc.payout_breakdown;
                                              const transferCurrency = dcTx.currency || currencyFromBalance;
                                              const originalCurrency = dcBd?.gross_currency;
                                              const originalAmount = dcBd?.gross_amount;
                                              const showOriginal = originalCurrency && originalAmount > 0 && originalCurrency !== transferCurrency;
                                              return (
                                                <Paper
                                                  elevation={0}
                                                  sx={{
                                                    mb: 1,
                                                    p: 2,
                                                    backgroundColor: alpha(theme.palette.warning.main, 0.06),
                                                    border: '1px dashed',
                                                    borderColor: alpha(theme.palette.warning.main, 0.45)
                                                  }}
                                                >
                                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Box sx={{ flex: 1 }}>
                                                      <Typography variant="body2" fontWeight="bold" color="warning.dark">
                                                        {formatCurrency(dcTx.amount, transferCurrency)}
                                                        {showOriginal && (
                                                          <Typography component="span" variant="caption" sx={{ ml: 1, color: 'warning.main', fontWeight: 400 }}>
                                                            ({formatCurrency(originalAmount, originalCurrency)})
                                                          </Typography>
                                                        )}
                                                      </Typography>
                                                      <Typography variant="caption" color="warning.main">
                                                        {translatePaymentType('damage_compensation')} • {formatDateWithTime(dcTx.date)}
                                                      </Typography>
                                                      <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', fontStyle: 'italic' }}>
                                                        Virement Stripe séparé — non inclus dans ce total
                                                      </Typography>
                                                    </Box>
                                                    <Chip
                                                      label={translatePaymentType('damage_compensation')}
                                                      size="small"
                                                      color="warning"
                                                      variant="outlined"
                                                    />
                                                  </Box>
                                                </Paper>
                                              );
                                            })()}

                                          {/* Actions */}
                                          {reservation.reservation_id && reservation.reservation_id !== 'unknown' && (
                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
                                              <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<VisibilityIcon />}
                                                onClick={() => handleShowReservationDetails(reservation.reservation_id)}
                                              >
                                                {t('stripe.details.seeReservation')}
                                              </Button>
                                            </Box>
                                          )}
                                        </CardContent>
                                      </Collapse>
                                    </Card>
                                  );
                                });

                                return cardsToRender;
                              })()}

                              {/* Bouton "Charger plus" - Pagination infinie */}
                              {(() => {
                                const hasMoreData = detailsData.pagination && detailsData.pagination.hasMore;
                                console.log(`🔍 [LoadMore Debug] Condition check for ${expandedBalance}:`, {
                                  detailsData: detailsData,
                                  pagination: detailsData.pagination,
                                  hasMore: detailsData.pagination?.hasMore,
                                  hasMoreData,
                                  willShowButton: hasMoreData
                                });
                                
                                return hasMoreData ? (
                                  <Box 
                                    display="flex" 
                                    justifyContent="center" 
                                    mt={3}
                                    sx={{
                                      borderTop: '1px solid',
                                      borderColor: 'divider',
                                      pt: 2
                                    }}
                                  >
                                    <Button 
                                      variant="outlined" 
                                      color="primary"
                                      size="large"
                                      startIcon={isLoadingDetails ? <CircularProgress size={16} /> : <ExpandMoreIcon />}
                                      onClick={() => {
                                        const [currency, balanceType] = expandedBalance.split('_');
                                        loadMoreDetails(currency, balanceType);
                                      }}
                                      disabled={isLoadingDetails}
                                      sx={{
                                        minWidth: 200,
                                        py: 1.5,
                                        '&:hover': {
                                          backgroundColor: alpha(theme.palette.primary.main, 0.1)
                                        }
                                      }}
                                    >
                                      {isLoadingDetails ? (
                                        t('stripe.details.loadingMore')
                                      ) : (
                                        <>
                                          {t('stripe.details.loadMore')}
                                          {detailsData.pagination.total && detailsData.items.length && (
                                            t('stripe.details.loadMoreCount', {count: detailsData.pagination.total - detailsData.items.length})
                                          )}
                                        </>
                                      )}
                                    </Button>
                                  </Box>
                                ) : (
                                  <Box 
                                    display="flex" 
                                    justifyContent="center" 
                                    mt={3}
                                    sx={{
                                      borderTop: '1px solid',
                                      borderColor: 'divider',
                                      pt: 2
                                    }}
                                  >
                                    <Typography variant="body2" color="text.secondary" align="center">
                                      {t('stripe.details.allLoaded')}
                                      {detailsData.pagination && (
                                        <>{t('stripe.details.allLoadedCount', {count: detailsData.pagination.total})}</>
                                      )}
                                    </Typography>
                                  </Box>
                                );
                              })()}

                              {/* Informations de pagination */}
                              {detailsData.pagination && (
                                <Box 
                                  display="flex" 
                                  justifyContent="center" 
                                  mt={2}
                                >
                                  <Typography variant="caption" color="text.secondary" align="center">
                                    {t('stripe.details.paginationShown', {shown: detailsData.items.length, total: detailsData.pagination.total || ''})}
                                  </Typography>
                                </Box>
                              )}
                            </>
                          ) : (
                            <Box textAlign="center" py={4}>
                              <Typography variant="body1" color="text.secondary">
                                {t('stripe.details.noData')}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            )}
          </React.Fragment>
        ))}

        {/* Section conversion totale */}
        <Divider sx={{ my: 3 }} />
        
        <Card 
          variant="outlined"
          sx={{ 
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            border: 2,
            borderColor: 'primary.main'
          }}
        >
          <CardContent>
            {/* En-tête conversion */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <ConvertIcon color="primary" />
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {t('stripe.conversion.title', 'Totaux convertis')}
                </Typography>
              </Box>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>{t('stripe.conversion.target', 'Convertir vers')}</InputLabel>
                <Select
                  value={targetCurrency}
                  onChange={handleTargetCurrencyChange}
                  label={t('stripe.conversion.target', 'Convertir vers')}
                >
                  {stripeData.available_currencies.map((currency) => (
                    <MenuItem key={currency} value={currency}>
                      {currency}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Métriques converties */}
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} sm={4}>
                <BalanceMetric
                  title={t('stripe.conversion.currentTotal', 'Total solde actuel')}
                  value={stripeData.converted_totals.current_balance}
                  currency={stripeData.converted_totals.currency}
                  icon={<CurrentIcon />}
                  color="success"
                  infoType="currentBalance"
                  balanceType="current"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <BalanceMetric
                  title={t('stripe.conversion.pendingTotal', 'Total à venir')}
                  value={stripeData.converted_totals.pending_balance}
                  currency={stripeData.converted_totals.currency}
                  icon={<PendingIcon />}
                  color="warning"
                  infoType="pendingBalance"
                  balanceType="pending"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <BalanceMetric
                  title={t('stripe.conversion.transferredTotal', 'Total transféré')}
                  value={stripeData.converted_totals.transferred_balance}
                  currency={stripeData.converted_totals.currency}
                  icon={<TransferredIcon />}
                  color="info"
                  infoType="transferredBalance"
                  balanceType="transferred"
                />
              </Grid>
            </Grid>

            {/* Informations sur les taux de change */}
            <Box 
              sx={{ 
                mt: 2, 
                p: 1.5, 
                backgroundColor: alpha(theme.palette.info.main, 0.1),
                borderRadius: 1,
                border: 1,
                borderColor: 'info.main'
              }}
            >
              <Typography variant="caption" color="info.main" fontWeight="bold">
                {t('stripe.conversion.note', 'Note')}: 
              </Typography>
              <Typography variant="caption" color="text.secondary" ml={1}>
                {t('stripe.conversion.rateInfo', 'Taux de change approximatifs, peuvent varier selon les fluctuations du marché.')}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Modal d'information */}
        <Dialog open={isInfoModalOpen} onClose={closeInfoModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            {infoModalContent.title}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              {infoModalContent.content}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeInfoModal} color="primary">
              {t('stripe.close')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal de détails de réservation */}
        {selectedReservationId && (
          <ReservationDetailsModal
            open={isModalOpen}
            onClose={handleCloseModal}
            reservationId={selectedReservationId}
          />
        )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default OwnerStripeBalancesWidget;